import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTenantCollection, useClubContent } from '../hooks/useTenantData';
import { useClub } from '../contexts/ClubContext';

// Component for flippable player spotlight card
function FlipCard({ player, clubName }) {
  const [isFlipped, setIsFlipped] = useState(false);
  
  const handleFlip = () => {
    setIsFlipped(!isFlipped);
  };
  
  // Default skill ratings if not provided
  const skills = player.skills || {
    dribbling: Math.floor(Math.random() * 30) + 60,
    passing: Math.floor(Math.random() * 30) + 60,
    shooting: Math.floor(Math.random() * 30) + 60,
    defense: Math.floor(Math.random() * 30) + 60,
    attitude: Math.floor(Math.random() * 20) + 75,
    teamwork: Math.floor(Math.random() * 20) + 75
  };
  
  const getSkillColor = (value) => {
    if (value >= 85) return '#22c55e'; // Green
    if (value >= 70) return '#f59e0b'; // Orange
    return '#ef4444'; // Red
  };

  return (
    <div 
      className={`player-card flip-card ${isFlipped ? 'flipped' : ''}`} 
      onClick={handleFlip}
    >
      <div className="flip-card-inner">
        {/* Front of Card */}
        <div className="flip-card-front">
          <div className="card-inner">
            <div className="card-header">
              <span className="team-name">{player.team}</span>
              <span className="flip-hint">Tap to flip</span>
            </div>
            
            <div className="card-image-container">
              <img src={player.image} alt={player.name} className="card-image" />
              <div className="card-number">#{player.number}</div>
            </div>
            
            <div className="card-info">
              <h3 className="player-name">{player.name}</h3>
              <span className="player-position">{player.position}</span>
            </div>

            <div className="card-stats">
              <div className="stat-item">
                <span className="stat-value">{player.goals}</span>
                <span className="stat-name">Goals</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">{player.assists}</span>
                <span className="stat-name">Assists</span>
              </div>
              <div className="stat-item">
                <span className="stat-value region">{player.region || 'TT'}</span>
                <span className="stat-name">Region</span>
              </div>
            </div>

            <div className="card-footer">
              <span className="footer-badge">⚽ {clubName}</span>
            </div>
          </div>
        </div>
        
        {/* Back of Card */}
        <div className="flip-card-back">
          <div className="card-inner">
            <div className="card-header">
              <span className="team-name">SKILL RATINGS</span>
            </div>
            
            <div className="skills-container">
              <div className="skill-row">
                <span className="skill-name">Dribbling</span>
                <div className="skill-bar">
                  <div 
                    className="skill-fill" 
                    style={{ width: `${skills.dribbling}%`, backgroundColor: getSkillColor(skills.dribbling) }}
                  ></div>
                </div>
                <span className="skill-value">{skills.dribbling}</span>
              </div>
              
              <div className="skill-row">
                <span className="skill-name">Passing</span>
                <div className="skill-bar">
                  <div 
                    className="skill-fill" 
                    style={{ width: `${skills.passing}%`, backgroundColor: getSkillColor(skills.passing) }}
                  ></div>
                </div>
                <span className="skill-value">{skills.passing}</span>
              </div>
              
              <div className="skill-row">
                <span className="skill-name">Shooting</span>
                <div className="skill-bar">
                  <div 
                    className="skill-fill" 
                    style={{ width: `${skills.shooting}%`, backgroundColor: getSkillColor(skills.shooting) }}
                  ></div>
                </div>
                <span className="skill-value">{skills.shooting}</span>
              </div>
              
              <div className="skill-row">
                <span className="skill-name">Defense</span>
                <div className="skill-bar">
                  <div 
                    className="skill-fill" 
                    style={{ width: `${skills.defense}%`, backgroundColor: getSkillColor(skills.defense) }}
                  ></div>
                </div>
                <span className="skill-value">{skills.defense}</span>
              </div>
              
              <div className="skill-row">
                <span className="skill-name">Attitude</span>
                <div className="skill-bar">
                  <div 
                    className="skill-fill" 
                    style={{ width: `${skills.attitude}%`, backgroundColor: getSkillColor(skills.attitude) }}
                  ></div>
                </div>
                <span className="skill-value">{skills.attitude}</span>
              </div>
              
              <div className="skill-row">
                <span className="skill-name">Teamwork</span>
                <div className="skill-bar">
                  <div 
                    className="skill-fill" 
                    style={{ width: `${skills.teamwork}%`, backgroundColor: getSkillColor(skills.teamwork) }}
                  ></div>
                </div>
                <span className="skill-value">{skills.teamwork}</span>
              </div>
            </div>
            
            {player.coachComment && (
              <div className="coach-comment">
                <span className="comment-label">Coach's Note:</span>
                <p>"{player.coachComment}"</p>
              </div>
            )}
            
            <div className="card-footer">
              <span className="footer-badge">⚽ {clubName}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Fallback player data (shown if Firestore has no players)
const defaultPlayers = [
  {
    id: 1,
    name: "Marcus Johnson",
    position: "Forward",
    number: 10,
    team: "U16 Tier 1",
    age: 15,
    goals: 12,
    assists: 8,
    region: "Port of Spain",
    image: "https://images.unsplash.com/photo-1517466787929-bc90951d0974?w=400&h=500&fit=crop",
    skills: { dribbling: 88, passing: 75, shooting: 92, defense: 45, attitude: 95, teamwork: 85 },
    coachComment: "Natural goal scorer with excellent finishing."
  },
  {
    id: 2,
    name: "Andre Williams",
    position: "Midfielder",
    number: 8,
    team: "U14 Tier 1",
    age: 13,
    goals: 6,
    assists: 15,
    region: "San Fernando",
    image: "https://images.unsplash.com/photo-1552058544-f2b08422138a?w=400&h=500&fit=crop",
    skills: { dribbling: 82, passing: 91, shooting: 68, defense: 72, attitude: 90, teamwork: 95 },
    coachComment: "Outstanding vision and passing ability."
  },
  {
    id: 3,
    name: "Kyle Baptiste",
    position: "Defender",
    number: 4,
    team: "U18 Tier 1",
    age: 17,
    goals: 2,
    assists: 4,
    region: "Arima",
    image: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=400&h=500&fit=crop",
    skills: { dribbling: 65, passing: 78, shooting: 55, defense: 94, attitude: 88, teamwork: 92 },
    coachComment: "Rock solid defender, reads the game well."
  },
  {
    id: 4,
    name: "Daniel Singh",
    position: "Goalkeeper",
    number: 1,
    team: "U16 Tier 2",
    age: 15,
    goals: 0,
    assists: 1,
    region: "Chaguanas",
    image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=500&fit=crop",
    skills: { dribbling: 45, passing: 72, shooting: 30, defense: 96, attitude: 92, teamwork: 88 },
    coachComment: "Quick reflexes and great shot-stopping."
  },
  {
    id: 5,
    name: "Joshua Thomas",
    position: "Forward",
    number: 9,
    team: "U12 Tier 1",
    age: 11,
    goals: 18,
    assists: 5,
    region: "Tunapuna",
    image: "https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=400&h=500&fit=crop",
    skills: { dribbling: 85, passing: 70, shooting: 88, defense: 40, attitude: 98, teamwork: 80 },
    coachComment: "Exciting young talent with lots of potential."
  },
  {
    id: 6,
    name: "Ryan Mohammed",
    position: "Midfielder",
    number: 7,
    team: "U14 Tier 1",
    age: 13,
    goals: 9,
    assists: 11,
    region: "San Juan",
    image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&h=500&fit=crop",
    skills: { dribbling: 80, passing: 85, shooting: 78, defense: 65, attitude: 90, teamwork: 94 },
    coachComment: "Box-to-box midfielder with great work rate."
  }
];

// Default homepage content (fallback) - uses club name dynamically
const defaultHomepageContent = {
  heroTitle: '', // Will be set from club.name
  heroSubtitle: 'Welcome to Our Club',
  statPlayers: '500+',
  statAgeGroups: '8',
  statCoaches: '50+',
  statTournaments: '25+',
  highlightVideo1: '',
  highlightVideo2: '',
  highlightTitle1: 'Match Highlights',
  highlightTitle2: 'Training Session'
};

// Sample highlight videos (fallback when no videos in Firestore)
const defaultHighlightVideos = [
  {
    id: 1,
    title: "January 2026 Highlights",
    month: "January",
    year: "2026",
    thumbnail: "https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=600&h=340&fit=crop",
    duration: "4:32",
    views: "1.2K",
    description: "Best goals, saves, and plays from January"
  },
  {
    id: 2,
    title: "December 2025 Highlights",
    month: "December",
    year: "2025",
    thumbnail: "https://images.unsplash.com/photo-1551958219-acbc608c6377?w=600&h=340&fit=crop",
    duration: "5:18",
    views: "2.4K",
    description: "Championship finals and holiday tournament action"
  },
  {
    id: 3,
    title: "November 2025 Highlights",
    month: "November",
    year: "2025",
    thumbnail: "https://images.unsplash.com/photo-1431324155629-1a6deb1dec8d?w=600&h=340&fit=crop",
    duration: "3:45",
    views: "1.8K",
    description: "Inter-club friendlies and training highlights"
  }
];

function Home() {
  const { club } = useClub();
  
  // Use tenant-scoped data hooks
  const { data: tenantPlayers, loading: playersLoading } = useTenantCollection('players');
  const { content: homepageContent, loading: contentLoading } = useClubContent('homepage');
  
  // DEBUG: Log what we're getting
  console.log('Home page - club:', club);
  console.log('Home page - homepageContent:', homepageContent);
  
  // Use tenant players if available, otherwise fallback to defaults
  const players = tenantPlayers.length > 0 ? tenantPlayers : defaultPlayers;
  
  // Merge homepage content with defaults, using club name as fallback for heroTitle
  const homepage = { 
    ...defaultHomepageContent, 
    ...homepageContent,
    // Always use club name if no heroTitle is set
    heroTitle: homepageContent?.heroTitle || club?.name?.toUpperCase() || 'WELCOME'
  };
  
  console.log('Home page - final heroTitle:', homepage.heroTitle);
  
  const [highlightVideos, setHighlightVideos] = useState(defaultHighlightVideos);
  const [currentSlide, setCurrentSlide] = useState(0);
  const cardsToShow = 3;
  const maxSlide = Math.max(0, players.length - cardsToShow);

  // Build highlight videos from homepage content
  useEffect(() => {
    if (homepageContent?.highlightVideo1 || homepageContent?.highlightVideo2) {
      const firestoreVideos = [];
      if (homepageContent.highlightVideo1) {
        firestoreVideos.push({
          id: 'yt1',
          title: homepageContent.highlightTitle1 || 'Match Highlights',
          embedUrl: homepageContent.highlightVideo1,
          isEmbed: true
        });
      }
      if (homepageContent.highlightVideo2) {
        firestoreVideos.push({
          id: 'yt2',
          title: homepageContent.highlightTitle2 || 'Training Session',
          embedUrl: homepageContent.highlightVideo2,
          isEmbed: true
        });
      }
      if (firestoreVideos.length > 0) {
        setHighlightVideos(firestoreVideos);
      }
    }
  }, [homepageContent]);

  useEffect(() => {
    if (players.length <= cardsToShow) return;
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev >= maxSlide ? 0 : prev + 1));
    }, 3000);
    return () => clearInterval(timer);
  }, [maxSlide, players.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev >= maxSlide ? 0 : prev + 1));
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev <= 0 ? maxSlide : prev - 1));
  };

  return (
    <>
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-content">
          <h1 className="hero-title">
            {(() => {
              const title = homepage.heroTitle;
              const words = title.split(' ');
              if (words.length === 1) {
                return <span className="title-top">{title}</span>;
              }
              return (
                <>
                  <span className="title-top">{words.slice(0, -1).join(' ')}</span>
                  <span className="title-bottom">{words.slice(-1)[0]}</span>
                </>
              );
            })()}
          </h1>
          <p className="hero-subtitle">
            {homepage.heroSubtitle || 'Join the premier youth football program. From U8 to Tier 2, we develop champions on and off the field.'}
          </p>
          <div className="hero-buttons">
            <Link to="/register" className="btn-primary">GET STARTED →</Link>
            <a href="#features" className="btn-secondary">LEARN MORE</a>
          </div>
        </div>

        <div className="stats-row">
          <div className="stat-card">
            <span className="stat-number">{homepage.statPlayers || '500+'}</span>
            <span className="stat-label">PLAYERS</span>
          </div>
          <div className="stat-card">
            <span className="stat-number">{homepage.statAgeGroups || '8'}</span>
            <span className="stat-label">AGE GROUPS</span>
          </div>
          <div className="stat-card">
            <span className="stat-number">{homepage.statCoaches || '50+'}</span>
            <span className="stat-label">COACHES</span>
          </div>
          <div className="stat-card">
            <span className="stat-number">{homepage.statTournaments || '25+'}</span>
            <span className="stat-label">TOURNAMENTS</span>
          </div>
        </div>
      </section>

      {/* Player Spotlight Section */}
      <section className="player-spotlight">
        <h2 className="section-title">PLAYER SPOTLIGHT</h2>
        <p className="section-subtitle">Recognizing outstanding performance across our programs</p>

        <div className="carousel-container">
          <button className="carousel-btn prev" onClick={prevSlide}>❮</button>
          
          <div className="carousel-track-container">
            <div 
              className="carousel-track"
              style={{ transform: `translateX(-${currentSlide * (100 / cardsToShow)}%)` }}
            >
              {players.map((player) => (
                <FlipCard 
                  key={player.id} 
                  player={player} 
                  clubName={club?.name || 'ClubLink TT'} 
                />
              ))}
            </div>
          </div>

          <button className="carousel-btn next" onClick={nextSlide}>❯</button>
        </div>

        <div className="carousel-dots">
          {Array.from({ length: maxSlide + 1 }).map((_, index) => (
            <span 
              key={index} 
              className={`dot ${currentSlide === index ? 'active' : ''}`}
              onClick={() => setCurrentSlide(index)}
            ></span>
          ))}
        </div>
      </section>

      {/* Monthly Highlights Section */}
      <section className="highlights-section">
        <h2 className="section-title">MONTHLY HIGHLIGHTS</h2>
        <p className="section-subtitle">Relive the best moments from our players each month</p>

        <div className="highlights-grid">
          {highlightVideos.map((video) => (
            video.isEmbed ? (
              // YouTube embed from Firestore
              <div className="video-card video-embed-card" key={video.id}>
                <div className="video-embed-container">
                  <iframe
                    src={video.embedUrl}
                    title={video.title}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
                <div className="video-info">
                  <h3 className="video-title">{video.title}</h3>
                </div>
              </div>
            ) : (
              // Fallback thumbnail cards
              <div className="video-card" key={video.id}>
                <div className="video-thumbnail">
                  <img src={video.thumbnail} alt={video.title} />
                  <div className="play-button">
                    <svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 24 24" fill="white">
                      <polygon points="5 3 19 12 5 21 5 3"></polygon>
                    </svg>
                  </div>
                  <div className="video-duration">{video.duration}</div>
                  <div className="video-month-badge">{video.month}</div>
                </div>
                <div className="video-info">
                  <h3 className="video-title">{video.title}</h3>
                  <p className="video-description">{video.description}</p>
                  <div className="video-meta">
                    <span className="video-views">👁 {video.views} views</span>
                    <span className="video-year">{video.year}</span>
                  </div>
                </div>
              </div>
            )
          ))}
        </div>

        <div className="highlights-cta">
          <button className="btn-secondary">VIEW ALL HIGHLIGHTS →</button>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section" id="features">
        <h2 className="section-title">EVERYTHING YOU NEED</h2>
        <p className="section-subtitle">A complete platform for players, parents, and coaches</p>

        <div className="features-grid">
          <Link to="/register" className="feature-card">
            <div className="feature-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                <circle cx="9" cy="7" r="4"></circle>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
              </svg>
            </div>
            <h3>PLAYER REGISTRATION</h3>
            <p>Easily register your players and manage their profiles across all age groups.</p>
          </Link>

          <Link to="/schedule" className="feature-card">
            <div className="feature-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="16" y1="2" x2="16" y2="6"></line>
                <line x1="8" y1="2" x2="8" y2="6"></line>
                <line x1="3" y1="10" x2="21" y2="10"></line>
              </svg>
            </div>
            <h3>SCHEDULE & EVENTS</h3>
            <p>View training sessions, matches, and tournaments on our interactive calendar.</p>
          </Link>

          <Link to="/kitshop" className="feature-card">
            <div className="feature-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path>
                <line x1="3" y1="6" x2="21" y2="6"></line>
                <path d="M16 10a4 4 0 0 1-8 0"></path>
              </svg>
            </div>
            <h3>KIT SHOP</h3>
            <p>Order official {club?.name || 'ClubLink TT'} jerseys, shorts, and accessories online.</p>
          </Link>

          <Link to="/schedule" className="feature-card">
            <div className="feature-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"></path>
                <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"></path>
                <path d="M4 22h16"></path>
                <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"></path>
                <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"></path>
                <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"></path>
              </svg>
            </div>
            <h3>TOURNAMENTS</h3>
            <p>Sign up for upcoming tournaments and track standings in real-time.</p>
          </Link>

          <Link to="/teams" className="feature-card">
            <div className="feature-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
              </svg>
            </div>
            <h3>PLAYER PROGRESS</h3>
            <p>Track development milestones and performance statistics for each player.</p>
          </Link>

          <Link to="/teams" className="feature-card">
            <div className="feature-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
              </svg>
            </div>
            <h3>COACH PORTAL</h3>
            <p>Coaches can manage attendance and grade players on key skills.</p>
          </Link>
        </div>
      </section>

      {/* Age Groups Section */}
      <section className="age-groups-section">
        <h2 className="section-title">AGE GROUPS</h2>
        <p className="section-subtitle">Programs tailored for every development stage</p>

        <div className="age-groups-grid">
          <button className="age-btn">U8</button>
          <button className="age-btn">U10</button>
          <button className="age-btn">U12</button>
          <button className="age-btn">U14</button>
          <button className="age-btn">U16</button>
          <button className="age-btn">U18</button>
          <button className="age-btn tier">Tier 1</button>
          <button className="age-btn tier">Tier 2</button>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <h2>READY TO JOIN?</h2>
        <p>Sign in to register your player, order kits, and join tournaments.</p>
        <Link to="/register" className="btn-primary">JOIN {club?.name?.toUpperCase() || 'THE CLUB'} →</Link>
      </section>
    </>
  );
}

export default Home;
